'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import NextImage from 'next/image';
import {
  getStrapiMediaUrl,
  getStrapiOriginalMediaUrl,
  isStrapiVideoMedia,
  isVideoUrl,
  type StrapiFlatMedia,
} from '@/lib/strapi-media-url';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { useMediaInViewport } from '@/hooks/useMediaInViewport';

interface MediaWithFallbackProps {
  media?: StrapiFlatMedia | null;
  posterMedia?: StrapiFlatMedia | null;
  videoUrl?: string;
  imageUrl?: string | null;
  alt?: string;
  className?: string;
  videoClassName?: string;
  imageClassName?: string;
  priority?: boolean;
  sizes?: string;
  fill?: boolean;
  width?: number;
  height?: number;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  poster?: string;
  /** When true with autoPlay off, show the first decoded video frame instead of hiding the video. */
  showFirstFrame?: boolean;
  onVideoLoad?: () => void;
  onVideoError?: () => void;
  // For background style usage
  asBackground?: boolean;
  backgroundStyle?: React.CSSProperties;
  children?: React.ReactNode;
  fallbackSrc?: string;
  /** Detach the video URL until this media enters the viewport. Defaults to true unless priority. */
  deferVideoLoad?: boolean;
}

export default function MediaWithFallback({
  media,
  posterMedia,
  videoUrl,
  imageUrl,
  alt = '',
  className = '',
  videoClassName = '',
  imageClassName = '',
  priority = false,
  sizes,
  fill = false,
  width,
  height,
  autoPlay = true,
  muted = true,
  loop = true,
  playsInline = true,
  poster,
  showFirstFrame = false,
  onVideoLoad,
  onVideoError,
  asBackground = false,
  backgroundStyle = {},
  children,
  fallbackSrc = MEDIA_PLACEHOLDER,
  deferVideoLoad,
}: MediaWithFallbackProps) {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const firstFrameRevealedRef = useRef(false);
  const shouldDeferVideo = deferVideoLoad ?? !priority;
  const { targetRef, shouldLoadMedia } = useMediaInViewport<HTMLDivElement>({
    disabled: !shouldDeferVideo,
  });

  const imageUrlIsVideo = isVideoUrl(imageUrl);
  const resolvedVideoUrl =
    videoUrl ||
    (isStrapiVideoMedia(media) ? getStrapiOriginalMediaUrl(media) || undefined : undefined) ||
    (imageUrlIsVideo ? imageUrl || undefined : undefined);
  const resolvedPoster =
    poster ||
    getStrapiMediaUrl(posterMedia, 'large') ||
    (!isStrapiVideoMedia(media) ? getStrapiMediaUrl(media, 'large') : null) ||
    (!imageUrlIsVideo ? imageUrl : null) ||
    fallbackSrc;
  const resolvedImageUrl =
    (!imageUrlIsVideo ? imageUrl : null) ||
    (!isStrapiVideoMedia(media) ? getStrapiMediaUrl(media, 'large') : null) ||
    resolvedPoster ||
    fallbackSrc;

  const displayImageUrl = imageError ? fallbackSrc : resolvedImageUrl || fallbackSrc;

  // Determine if we should try to load video
  const shouldLoadVideo = Boolean(resolvedVideoUrl && !hasVideoError && shouldLoadMedia);
  const isVideoFile = Boolean(
    resolvedVideoUrl &&
    (isStrapiVideoMedia(media) ||
      isVideoUrl(resolvedVideoUrl) ||
      resolvedVideoUrl.toLowerCase().includes('video')),
  );

  const revealPausedFirstFrame = useCallback(
    (video: HTMLVideoElement) => {
      setIsVideoLoaded(true);
      setShowVideo(true);

      if (firstFrameRevealedRef.current || !video.paused) {
        onVideoLoad?.();
        return;
      }

      firstFrameRevealedRef.current = true;
      video.pause();
      if (video.currentTime < 0.01) {
        video.currentTime = 0.001;
      }
      onVideoLoad?.();
    },
    [onVideoLoad],
  );

  useEffect(() => {
    firstFrameRevealedRef.current = false;
  }, [resolvedVideoUrl]);

  useEffect(() => {
    setImageError(false);
    setImageLoaded(!resolvedImageUrl || resolvedImageUrl === fallbackSrc);
  }, [resolvedImageUrl, fallbackSrc]);

  useEffect(() => {
    if (shouldLoadVideo && isVideoFile) {
      const video = videoRef.current;

      if (video) {
        const handleCanPlay = () => {
          if (showFirstFrame && !autoPlay) {
            revealPausedFirstFrame(video);
            return;
          }
          setIsVideoLoaded(true);
          setTimeout(() => {
            setShowVideo(true);
            if (autoPlay && video.paused) {
              video.play().catch(console.warn);
            }
          }, 100);
          onVideoLoad?.();
        };

        const handleLoadedData = () => {
          if (showFirstFrame && !autoPlay) {
            revealPausedFirstFrame(video);
            return;
          }
          setIsVideoLoaded(true);
          if (autoPlay && video.paused) {
            video.play().catch(console.warn);
          }
        };

        const handleError = () => {
          setHasVideoError(true);
          setIsVideoLoaded(false);
          setShowVideo(false);
          onVideoError?.();
        };

        video.addEventListener('canplaythrough', handleCanPlay);
        video.addEventListener('loadeddata', handleLoadedData);
        video.addEventListener('error', handleError);

        if (video.readyState >= 2 && showFirstFrame && !autoPlay) {
          revealPausedFirstFrame(video);
        } else if (video.readyState >= 3) {
          setIsVideoLoaded(true);
          setTimeout(() => {
            setShowVideo(true);
            if (autoPlay && video.paused) {
              video.play().catch(console.warn);
            }
          }, 50);
        } else if (video.readyState === 0) {
          video.load();
        }

        setTimeout(() => {
          if (video.readyState === 0) {
            video.load();
          } else if (video.readyState >= 2 && showFirstFrame && !autoPlay && !showVideo) {
            revealPausedFirstFrame(video);
          } else if (video.readyState >= 3 && !showVideo) {
            setIsVideoLoaded(true);
            setShowVideo(true);
            if (autoPlay && video.paused) {
              video.play().catch(console.warn);
            }
          }
        }, 100);

        return () => {
          video.removeEventListener('canplaythrough', handleCanPlay);
          video.removeEventListener('loadeddata', handleLoadedData);
          video.removeEventListener('error', handleError);
        };
      }
    }
  }, [
    shouldLoadVideo,
    isVideoFile,
    autoPlay,
    showFirstFrame,
    onVideoLoad,
    onVideoError,
    showVideo,
    revealPausedFirstFrame,
  ]);

  useEffect(() => {
    if (shouldLoadVideo && isVideoFile && showVideo) {
      const video = videoRef.current;
      if (video && autoPlay && video.paused) {
        const timer = setTimeout(() => {
          video.play().catch(console.warn);
        }, 200);
        return () => clearTimeout(timer);
      }
    }
  }, [showVideo, shouldLoadVideo, isVideoFile, autoPlay]);

  // Background style mode
  if (asBackground) {
    const finalBackgroundStyle = {
      ...backgroundStyle,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      ...(!backgroundStyle.backgroundImage && {
        backgroundImage: `url('${displayImageUrl || fallbackSrc}')`,
      }),
    };

    return (
      <div ref={targetRef} className={className} style={finalBackgroundStyle}>
        {shouldLoadVideo && isVideoFile && (
          <video
            key={resolvedVideoUrl}
            ref={videoRef}
            src={resolvedVideoUrl}
            autoPlay={autoPlay}
            muted={muted}
            loop={loop}
            playsInline={playsInline}
            controls={false}
            disablePictureInPicture
            preload={priority ? 'auto' : 'metadata'}
            className={`pointer-events-none absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${videoClassName} ${
              showVideo ? 'opacity-100 z-0' : 'opacity-0 -z-10'
            }`}
            poster={resolvedPoster || fallbackSrc}
            onError={() => {
              setHasVideoError(true);
            }}
            onLoadedData={() => {
              const video = videoRef.current;
              if (video && autoPlay && video.paused) {
                video.play().catch((error) => {
                  console.warn('Background video autoplay failed:', error);
                });
              }
            }}
            onCanPlay={() => {
              const video = videoRef.current;
              if (video && autoPlay && video.paused) {
                video.play().catch((error) => {
                  console.warn('Background video play failed:', error);
                });
              }
            }}
          />
        )}
        {children}
      </div>
    );
  }

  const hideImageForVideo = showVideo || (showFirstFrame && isVideoLoaded);
  const showPlaceholderBg = !imageLoaded && !imageError && displayImageUrl !== fallbackSrc;

  return (
    <div ref={targetRef} className={`relative ${className}`}>
      {/* Image (or brand placeholder on error / missing URL). Hidden once video is ready. */}
      {!(showFirstFrame && shouldLoadVideo && !hasVideoError) && (
        <NextImage
          src={displayImageUrl}
          alt={alt}
          fill={fill}
          width={width}
          height={height}
          className={`${imageClassName} transition-opacity duration-300 ${
            hideImageForVideo ? 'opacity-0' : 'opacity-100'
          }`}
          priority={priority}
          sizes={sizes || (fill ? '100vw' : undefined)}
          quality={85}
          loading={priority ? 'eager' : 'lazy'}
          unoptimized={imageError || displayImageUrl === fallbackSrc}
          onError={() => {
            setImageError(true);
            setImageLoaded(true);
          }}
          onLoad={() => {
            setImageLoaded(true);
          }}
          style={{
            display: hideImageForVideo ? 'none' : 'block',
            ...(showPlaceholderBg
              ? {
                  backgroundImage: `url('${fallbackSrc}')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }
              : {}),
          }}
        />
      )}

      {/* Brand placeholder while video is buffering and no poster/image is shown yet */}
      {shouldLoadVideo && isVideoFile && !hideImageForVideo && !displayImageUrl && (
        <NextImage
          src={fallbackSrc}
          alt=""
          aria-hidden
          fill
          className={`object-cover ${imageClassName}`}
          sizes={sizes || '100vw'}
          unoptimized
        />
      )}

      {/* Video — fades in when ready; otherwise placeholder/image remains visible */}
      {shouldLoadVideo && isVideoFile && (
        <video
          key={resolvedVideoUrl}
          ref={videoRef}
          src={resolvedVideoUrl}
          autoPlay={autoPlay}
          muted={muted}
          loop={loop}
          playsInline={playsInline}
          controls={false}
          disablePictureInPicture
          preload={priority ? 'auto' : 'metadata'}
          className={`pointer-events-none absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${videoClassName} ${
            showVideo || (showFirstFrame && isVideoLoaded) ? 'opacity-100' : 'opacity-0'
          }`}
          poster={showFirstFrame ? undefined : resolvedPoster || fallbackSrc}
          style={{ display: showFirstFrame || showVideo ? 'block' : 'none' }}
          onError={() => {
            setHasVideoError(true);
          }}
          onLoadedData={() => {
            const video = videoRef.current;
            if (video && autoPlay && video.paused) {
              video.play().catch((error) => {
                console.warn('Video autoplay failed:', error);
              });
            }
          }}
          onCanPlay={() => {
            const video = videoRef.current;
            if (video && autoPlay && video.paused) {
              video.play().catch((error) => {
                console.warn('Video play failed:', error);
              });
            }
          }}
        >
          Your browser does not support the video tag.
        </video>
      )}
    </div>
  );
}
